import React, {Fragment} from 'react';
import moment from "moment";
import 'moment/locale/fr';
import createLink from "../../../libs/createLink";
import _ from "lodash";
import {cropText, isNoFollowProfil} from "../../../libs/helper";
import {USER_CURRENT_KEY} from "../../../api/public-voissa/users";
import {useQueryClient} from "@tanstack/react-query";
import {faCirclePlus, faSquarePollHorizontal, faComment, faHeart, faAngleDoubleRight, faEye, faCommentQuote} from "@fortawesome/pro-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import { Link } from 'react-router-dom';
import './Notification.scss';
import Avatar from '../../components/Avatar';
import classNames from "classnames";
import voissaAvatar from "../../../images/voissa-avatar.svg";
import { CONTENT_RATINGS_MAP } from '../../../const/media';
moment.locale('fr');

const NOTIFICATION_TYPES = {
    LIKE: {
        MEDIA: 'LIKE_MEDIA', //MANY TO ONE = FUSION KEY
        ARTICLE: 'LIKE_ARTICLE', //MANY TO ONE
        STATUS: 'LIKE_STATUS', //MANY TO ONE
        THREAD: 'LIKE_THREAD', //MANY TO ONE
        THREAD_COMMENT: 'LIKE_THREAD_COMMENT', //MANY TO ONE
        COMMENT: {
            MEDIA: 'LIKE_COMMENT_MEDIA', //MANY TO ONE
            ARTICLE: 'LIKE_COMMENT_ARTICLE', //MANY TO ONE
            STATUS: 'LIKE_COMMENT_STATUS' //MANY TO ONE
        }
    },
    COMMENT: {
        MEDIA: 'COMMENT_MEDIA', //MANY TO ONE, ONE TO ONE (si subcomment ONE TO ONE)
        ARTICLE: 'COMMENT_ARTICLE', //MANY TO ONE, ONE TO ONE (si subcomment ONE TO ONE)
        STATUS: 'COMMENT_STATUS',  //MANY TO ONE, ONE TO ONE (si subcomment ONE TO ONE)
        RESPONSE: 'COMMENT_RESPONSE' //MANY TO ONE
    },
    MENTION: {
        COMMENT : 'MENTION_COMMENT'
    },
    SOCIAL: {
        CIRCLE: 'ADD_CIRCLE', //ONE TO ONE
        FOLLOW: 'ADD_FOLLOW', //MANY TO ONE
        FOLLOW_BACK: 'ADD_FOLLOW_BACK' //ONE TO ONE
    },
    THREAD: {
        RESPONSE: 'THREAD_RESPONSE', //MANY TO ONE
        CITATION: 'THREAD_CITATION' // ONE TO ONE
    },
    STATUS: {
        CREATE: 'CREATE_STATUS' //MANY TO ONE, ONE TO ONE
    },
    VIEW: {
        PROFILE: 'VIEW_PROFILE' //MANY TO ONE
    },
    MEDIA: {
        VIDEO_ENCODING_FINISHED: 'MEDIA_VIDEO_ENCODING_FINISHED',
        CONTENT_RATING_UPDATED: 'MEDIA_CONTENT_RATING_UPDATED'
    }
};


/**
 * Si owner est non l'autheur du status, on envoie une notification
 * X a publié un message sur votre mur
 * X et Y ont publié un message sur votre mur
 *
 */

const hasMultipleSender = (fSenders) =>{
    return (Array.isArray(fSenders) && fSenders.length > 1);
}
/*
const genderIsFemale = (gender = 1) =>{
    return [2, 4, 6].includes(gender);
}
*/

const NotificationItemUserLink = ({user}) => {
    if(user){
        let relLinkProfil = isNoFollowProfil(user) ? { rel: "nofollow" } : {};
        return <Link className='notification-sender-username' to={createLink("userProfile", user)} {...relLinkProfil}>{_.capitalize(user.username)}</Link>
    }
    return <span>compte supprimé</span>;
};

const NotificationItemSenders = ({mainSender, senders, cutLimit = 4}) => {
    if (senders.length <= 1) return <NotificationItemUserLink user={mainSender}/>;
    else if (senders.length <= cutLimit) {
        return (
            <Fragment>
                {senders.reverse().map((sender, i) => {
                    let separator = i === (senders.length - 2) ? ' et ' : (i === (senders.length - 1) ? ' ' : ', ');
                    return <Fragment key={sender._id}><NotificationItemUserLink user={sender}/>{separator}</Fragment>
                })}
            </Fragment>
        )
    } else if (senders.length > cutLimit) {
        let slicedSenders = senders.slice(0, cutLimit);
        let senderRestCount = senders.length - cutLimit;
        let isPlural = (senderRestCount === 1 ? '' : 's')
        return (
            <Fragment>
                {slicedSenders.map((sender, i) => {
                    let separator = (i === (slicedSenders.length - 1) ? ' ' : ', ');
                    return (
                        <Fragment key={sender._id}>
                            <NotificationItemUserLink user={sender}/>
                            {separator}
                        </Fragment>
                    )
                })}
                et {senderRestCount} autre{isPlural} personne{isPlural}
            </Fragment>
        )
    } else return null;
}

const renderers = {
    [NOTIFICATION_TYPES.LIKE.MEDIA]: {
        icon: faHeart,
        content: (notification) => {
            const isPlural = hasMultipleSender(notification.fSenders);
            return <Fragment>
                <NotificationItemSenders mainSender={notification.sender} senders={notification.fSenders}/>
                {isPlural ? ' ont' : ' a'} aimé votre publication {!!notification?.data?.title &&
                <b>{cropText(notification.data.title,50)}</b>}.
            </Fragment>
        },
        getLink: (notification) => createLink(notification.refKind === 'video' ? 'mediaviewerVideo' : 'mediaviewerPhoto', {
            media: {
                _id: notification.refId,
                slug: notification?.data?.slug
            }
        })
    },
    [NOTIFICATION_TYPES.LIKE.ARTICLE]: {
        icon: faHeart,
        content: (notification) => {
            const isPlural = hasMultipleSender(notification.fSenders);
            return <Fragment>
                <NotificationItemSenders mainSender={notification.sender} senders={notification.fSenders}/>
                {isPlural ? ' ont' : ' a'} aimé votre billet {!!notification?.data?.title &&
                <b>{cropText(notification.data.title,50)}</b>}.
            </Fragment>
        },
        getLink: (notification) => createLink('article', {
            slug: _.get(notification,"owner.slug") || "",
            article: {
                _id: notification.refId,
                slug: notification?.data?.slug
            }
        })
    },
    [NOTIFICATION_TYPES.LIKE.STATUS]: {
        icon: faHeart,
        content: (notification) => {
            const isPlural = hasMultipleSender(notification.fSenders);
            return <Fragment>
                <NotificationItemSenders mainSender={notification.sender} senders={notification.fSenders}/>
                {isPlural ? ' ont' : ' a'} aimé votre statut.
            </Fragment>
        },
        getLink: (notification) => notification.owner ? createLink('userProfile', notification.owner) : null,
        getState: (notification) => {
            return {
                statusId : notification?.refId
            }
        }
    },
    [NOTIFICATION_TYPES.LIKE.THREAD]: {
        icon: faHeart,
        content: (notification) => {
            const isPlural = hasMultipleSender(notification.fSenders);
            return <Fragment>
                <NotificationItemSenders mainSender={notification.sender} senders={notification.fSenders}/>
                {isPlural ? ' ont' : ' a'} aimé votre thread {!!notification?.data?.title &&
                <b>{cropText(notification.data.title,50)}</b>}.
            </Fragment>
        },
        getLink: (notification) => createLink("forumTopic", {
            categoryParent : {slug : notification?.data?.forumParent?.slug},
            category : {slug : notification?.data?.forum?.slug},
            topic : {_id : notification.refId, slug : notification?.data?.slug}
        })
    },
    [NOTIFICATION_TYPES.LIKE.THREAD_COMMENT]: {
        icon: faHeart,
        content: (notification) => {
            const isPlural = hasMultipleSender(notification.fSenders);
            return <Fragment>
                <NotificationItemSenders mainSender={notification.sender} senders={notification.fSenders}/>
                {isPlural ? ' ont' : ' a'} aimé votre commentaire sur le thread {!!notification?.data?.topic?.title &&
                <b>{cropText(notification.data.topic.title,50)}</b>}.
            </Fragment>
        },
        getLink: (notification) => createLink("forumTopic", {
            categoryParent : {slug : notification?.data?.forumParent?.slug},
            category : {slug : notification?.data?.forum?.slug},
            topic : {_id : notification?.data?.topic?._id, slug : notification?.data?.topic?.slug}
        }),
        getState: (notification) => {
            return {
                commentId: notification?.data?.commentId,
            }
        }
    },
    [NOTIFICATION_TYPES.LIKE.COMMENT.MEDIA]: {
        icon: faHeart,
        content: (notification) => {
            const isPlural = hasMultipleSender(notification.fSenders);
            return <Fragment>
                <NotificationItemSenders mainSender={notification.sender} senders={notification.fSenders}/>
                {isPlural ? ' ont' : ' a'} aimé votre commentaire {!!notification?.data?.media?.title &&
                <b>{cropText(notification.data.media.title,50)}</b>}.
            </Fragment>
        },
        getLink: (notification) => createLink(notification?.data?.media?.kind === 'video' ? 'mediaviewerVideo' : 'mediaviewerPhoto', {
            media: {
                _id: notification.data?.media?._id,
                slug: notification?.data?.media?.slug
            }
        }),
        getState: (notification) => {
            return {
                commentId: notification?.data?.isSubComment ? notification?.data?.commentParent?._id : notification?.data?.commentId,
                subCommentId: notification?.data?.isSubComment ? notification?.data?.commentId : null,
                toggleComment : true
            }
        }
    },
    [NOTIFICATION_TYPES.LIKE.COMMENT.ARTICLE]: {
        icon: faHeart,
        content: (notification) => {
            const isPlural = hasMultipleSender(notification.fSenders);
            return <Fragment>
                <NotificationItemSenders mainSender={notification.sender} senders={notification.fSenders}/>
                {isPlural ? ' ont' : ' a'} aimé votre commentaire {!!notification?.data?.article?.title &&
                <b>{cropText(notification.data.article.title,50)}</b>}.
            </Fragment>
        },
        getLink: (notification) => {
            const slugOwner = _.get(notification,"data.article.owner.slug") || _.get(notification,"owner.slug") || "";
            return createLink('article', {
                slug: slugOwner,
                article: {
                    _id: notification?.data?.article._id,
                    slug: notification?.data?.article?.slug
                }
            })
        },
        getState: (notification) => {
            return {
                commentId: notification?.data?.isSubComment ? notification?.data?.commentParent?._id : notification?.data?.commentId,
                subCommentId: notification?.data?.isSubComment ? notification?.data?.commentId : null,
                toggleComment : true
            }
        }
    },
    [NOTIFICATION_TYPES.LIKE.COMMENT.STATUS]: {
        icon: faHeart,
        content: (notification) => {
            const isPlural = hasMultipleSender(notification.fSenders);
            return <Fragment>
                <NotificationItemSenders mainSender={notification.sender} senders={notification.fSenders}/>
                {isPlural ? ' ont' : ' a'} aimé votre commentaire sur un statut.
            </Fragment>
        },
        getLink: (notification) => {
            const owner = notification?.data?.status?.owner || notification.owner || null;
            return owner ? createLink('userProfile', owner) : null
        },
        getState: (notification) => {
            return {
                statusId : notification?.data?.status?._id,
                commentId: notification?.data?.isSubComment ? notification?.data?.commentParent?._id : notification?.data?.commentId,
                subCommentId: notification?.data?.isSubComment ? notification?.data?.commentId : null,
                toggleComment : true
            }
        }
    },
    [NOTIFICATION_TYPES.SOCIAL.CIRCLE]: {
        icon: faCirclePlus,
        content: (notification) => {
            return <Fragment>
                <NotificationItemUserLink user={notification.sender}/> vous a ajouté à son cercle.
            </Fragment>
        },
        getLink: (notification) => createLink('userProfile', notification.sender)
    },
    [NOTIFICATION_TYPES.SOCIAL.FOLLOW]: {
        icon: faAngleDoubleRight,
        content: (notification) => {
            const isPlural = hasMultipleSender(notification.fSenders);
            return <Fragment>
                <NotificationItemSenders mainSender={notification.sender} senders={notification.fSenders}/> {isPlural ? ' ont' : ' a'}  suivi votre profil.
            </Fragment>
        },
        getLink: (notification)=>{
            return notification?.fSenders?.length === 1 ?  createLink('userProfile', notification.sender) : null
        }
    },
    [NOTIFICATION_TYPES.SOCIAL.FOLLOW_BACK]: {
        icon: faAngleDoubleRight,
        content: (notification) => {
            return <Fragment>
                <NotificationItemSenders mainSender={notification.sender} senders={notification.fSenders}/> vous suit en retour.
            </Fragment>
        },
        getLink: (notification) => createLink('userProfile', notification.sender)
    },
    [NOTIFICATION_TYPES.THREAD.RESPONSE]: {
        icon: faComment,
        content: (notification) => {
            const isPlural = hasMultipleSender(notification.fSenders);
            return <Fragment>
                <NotificationItemSenders mainSender={notification.sender} senders={notification.fSenders}/>
                {isPlural ? ' ont' : ' a'} répondu au thread {!!notification?.data?.topic?.title &&
                <b>{cropText(notification.data?.topic.title,50)}</b>}.
            </Fragment>
        },
        getLink: (notification) => createLink("forumTopic", {
            categoryParent : {slug : notification?.data?.forumParent?.slug},
            category : {slug : notification?.data?.forum?.slug},
            topic : {_id : notification?.data?.topic?._id, slug : notification?.data?.topic?.slug}
        }),
        getState: (notification) => {
            if(notification?.fSenders?.length > 1) return null;
            return {
                commentId: notification?.data?.commentId,
            }
        }
    },
    [NOTIFICATION_TYPES.THREAD.CITATION]: {
        icon: faCommentQuote,
        content: (notification) => {
            const isPlural = hasMultipleSender(notification.fSenders);
            return <Fragment>
                <NotificationItemSenders mainSender={notification.sender} senders={notification.fSenders}/>
                {` vous ${isPlural ? 'ont' : 'a'} répondu sur le thread`} {!!notification?.data?.topic?.title &&
                <b>{cropText(notification.data?.topic.title,50)}</b>}.
            </Fragment>
        },
        getLink: (notification) => createLink("forumTopic", {
            categoryParent : {slug : notification?.data?.forumParent?.slug},
            category : {slug : notification?.data?.forum?.slug},
            topic : {_id : notification?.data?.topic?._id, slug : notification?.data?.topic?.slug}
        }),
        getState: (notification) => {
            if(notification?.fSenders?.length > 1) return null;
            return {
                commentId: notification?.data?.commentId,
            }
        }
    },
    [NOTIFICATION_TYPES.STATUS.CREATE]: {
        icon: faSquarePollHorizontal,
        content: (notification) => {
            const isPlural = hasMultipleSender(notification.fSenders);
            return <Fragment>
                <NotificationItemSenders mainSender={notification.sender} senders={notification.fSenders}/>
                {isPlural ? ' ont' : ' a'} publié un message sur votre mur.
            </Fragment>
        },
        //getLink: (notification) => notification.refIds ? createLink('userNotificationOne', {...notification.owner, notification : {_id : notification._id}}) : createLink('userProfile', notification.owner)
        getLink: (notification) => notification.owner ? createLink('userProfile', notification.owner) : null,
        getState: (notification) => {
            if(notification?.fSenders?.length > 1) return null;
            return {
                statusId : notification?.refId
            }
        }
    },
    [NOTIFICATION_TYPES.COMMENT.MEDIA]: {
        icon: faComment,
        content: (notification) => {
            const isPlural = hasMultipleSender(notification.fSenders);
            return <Fragment>
                <NotificationItemSenders mainSender={notification.sender} senders={notification.fSenders}/>
                {isPlural ? ' ont' : ' a'} commenté votre publication {!!notification?.data?.parent?.title &&
                <b>{cropText(notification.data?.parent.title,50)}</b>}.
            </Fragment>
        },
        getLink: (notification) => createLink(notification?.data?.parent?.kind === "video" ? "mediaviewerVideo" : "mediaviewerPhoto", {
            media: {
                _id: notification?.data?.parent._id,
                slug: notification?.data?.parent?.slug
            }
        }),
        getState: (notification) => {
            if(notification?.fSenders?.length > 1) {
                return {
                    toggleComment : true
                }
            }
            else{
                return {
                    toggleComment : true,
                    //commentId : notification?.data?.commentId
                    commentId: notification?.data?.commentParent?._id ? notification?.data?.commentParent?._id : notification?.data?.commentId,
                    subCommentId: notification?.data?.commentParent?._id ? notification?.data?.commentId : null,
                }
            }
        }
    },
    [NOTIFICATION_TYPES.COMMENT.ARTICLE]: {
        icon: faComment,
        content: (notification) => {
            const isPlural = hasMultipleSender(notification.fSenders);
            return <Fragment>
                <NotificationItemSenders mainSender={notification.sender} senders={notification.fSenders}/>
                {isPlural ? ' ont' : ' a'} commenté votre billet {!!notification?.data?.parent?.title &&
                <b>{cropText(notification.data?.parent.title,50)}</b>}.
            </Fragment>
        },
        getLink: (notification) => createLink('article', {
            slug: _.get(notification,"owner.slug") || "",
            article: {
                _id: notification?.data?.parent._id,
                slug: notification?.data?.parent?.slug
            }
        }),
        getState: (notification) => {
            if(notification?.fSenders?.length > 1) {
                return {
                    toggleComment : true
                }
            }
            else{
                return {
                    toggleComment : true,
                    commentId: notification?.data?.commentParent?._id ? notification?.data?.commentParent?._id : notification?.data?.commentId,
                    subCommentId: notification?.data?.commentParent?._id ? notification?.data?.commentId : null,
                }
            }
        }
    },
    [NOTIFICATION_TYPES.COMMENT.STATUS]: {
        icon: faComment,
        content: (notification) => {
            const isPlural = hasMultipleSender(notification.fSenders);
            return <Fragment>
                <NotificationItemSenders mainSender={notification.sender} senders={notification.fSenders}/>
                {isPlural ? ' ont' : ' a'} commenté votre statut.
            </Fragment>
        },
        //getLink: (notification) => notification.refIds ? createLink('userNotificationOne', {...notification.owner, notification : {_id : notification._id}}) : createLink('userProfile', notification.owner),
        getLink: (notification) => notification.owner ? createLink('userProfile', notification.owner) : null,
        getState: (notification) => {
            if(notification?.fSenders?.length > 1) {
                return {
                    toggleComment : true,
                    statusId : notification?.data?.parent?._id
                }
            }
            else{
                return {
                    toggleComment : true,
                    statusId : notification?.data?.parent?._id,
                    commentId: notification?.data?.commentParent?._id ? notification?.data?.commentParent?._id : notification?.data?.commentId,
                    subCommentId: notification?.data?.commentParent?._id ? notification?.data?.commentId : null,
                }
            }
        }
    },
    [NOTIFICATION_TYPES.COMMENT.RESPONSE]: {
        icon: faComment,
        content: (notification) => {
            const isPlural = hasMultipleSender(notification.fSenders);
            return <Fragment>
                <NotificationItemSenders mainSender={notification.sender} senders={notification.fSenders}/>
                {isPlural ? ' ont' : ' a'} répondu à votre commentaire.
            </Fragment>
        },
        getLink: (notification) => {
            if(notification?.data?.type === "media"){
                return createLink(notification?.data?.parent?.kind === "video" ? "mediaviewerVideo" : "mediaviewerPhoto", {
                    media: {
                        _id: notification?.data?.parent._id,
                        slug: notification?.data?.parent?.slug
                    }
                })
            }
            else if(notification?.data?.type === "article"){
                const slugOwner = _.get(notification,"data.parent.owner.slug") || _.get(notification,"owner.slug") || ""
                return createLink('article', {
                    slug: slugOwner,
                    article: {
                        _id: notification?.data?.parent._id,
                        slug: notification?.data?.parent?.slug
                    }
                })
            }
            else if(notification?.data?.type === "status"){
                const owner = _.get(notification,"data.parent.owner") || _.get(notification,"owner") || null;
                //return notification.refIds ? createLink('userNotificationOne', {...notification.owner, notification : {_id : notification._id}}) : createLink('userProfile', notification.owner)
                return  owner ? createLink('userProfile', owner) : null;
            }
        },
        getState: (notification) => {
            const typeKey = {
                status : "statusId"
            }
            if(notification?.fSenders?.length > 1){
                return {
                    toggleComment : true
                }
            }
            else{
                if(typeKey[notification?.data?.type]){
                    return {
                        [typeKey[notification?.data?.type]] : notification?.data?.parent?._id,
                        commentId: notification?.data?.commentParent?._id,
                        subCommentId: notification?.data?.commentId,
                        toggleComment : true
                    }
                }
                else{
                    return {
                        commentId: notification?.data?.commentParent?._id,
                        subCommentId: notification?.data?.commentId,
                        toggleComment : true
                    }
                }
            }
        }
    },
    [NOTIFICATION_TYPES.MENTION.COMMENT]: {
        icon: faComment,
        content: (notification) => {
            return <Fragment>
                <NotificationItemSenders mainSender={notification.sender} senders={notification.fSenders}/>
                {" vous a mentionné dans un commentaire"}
            </Fragment>
        },
        getLink: (notification) => {
            if(notification?.data?.type === "media"){
                return createLink(notification?.data?.parent?.kind === "video" ? "mediaviewerVideo" : "mediaviewerPhoto", {
                    media: {
                        _id: notification?.data?.parent._id,
                        slug: notification?.data?.parent?.slug
                    }
                })
            }
            else if(notification?.data?.type === "article"){
                return createLink('article', {
                    slug: _.get(notification,"data.parent.owner.slug") || "",
                    article: {
                        _id: notification?.data?.parent._id,
                        slug: notification?.data?.parent?.slug
                    }
                })
            }
            else if(notification?.data?.type === "status"){
                const owner = notification?.data?.parent?.owner || notification.owner || null;
                //return notification.refIds ? createLink('userNotificationOne', {...notification.owner, notification : {_id : notification._id}}) : createLink('userProfile', notification.owner)
                return owner ? createLink('userProfile', owner) : null;
            }
        },
        getState: (notification) => {
            const typeKey = {
                status : "statusId"
            }
            if(notification?.fSenders?.length > 1){
                return {
                    toggleComment : true
                }
            }
            else{
                if(typeKey[notification?.data?.type]){
                    return {
                        [typeKey[notification?.data?.type]] : notification?.data?.parent?._id,
                        commentId: notification?.data?.isSubComment ? notification?.data?.commentParent?._id : notification?.data?.commentId,
                        subCommentId: notification?.data?.isSubComment ? notification?.data?.commentId : null,
                        toggleComment : true
                    }
                }
                else{
                    return {
                        commentId: notification?.data?.isSubComment ? notification?.data?.commentParent?._id : notification?.data?.commentId,
                        subCommentId: notification?.data?.isSubComment ? notification?.data?.commentId : null,
                        toggleComment : true
                    }
                }
            }
        }
    },
    [NOTIFICATION_TYPES.VIEW.PROFILE]: {
        icon: faEye,
        content: (notification) => {
            const isPlural = hasMultipleSender(notification.fSenders);
            return <Fragment>
                <NotificationItemSenders mainSender={notification.sender} senders={notification.fSenders}/> {isPlural ? ' ont' : ' a'} visité votre profil.
            </Fragment>
        },
        getLink: (notification) => createLink('userProfile', notification.sender)
    },

    [NOTIFICATION_TYPES.MEDIA.VIDEO_ENCODING_FINISHED]: {
        icon: (notification) => {
            const currentContentRatingTag = CONTENT_RATINGS_MAP[notification?.data?.contentRating] || null;
            return currentContentRatingTag.icon;
        },
        content: (notification) => {
            const currentContentRatingTag = CONTENT_RATINGS_MAP[notification?.data?.contentRating] || null;
            return (
                <Fragment>
                    Votre vidéo <b>{cropText(notification.data?.title, 50)}</b> est disponible et classée <b>{currentContentRatingTag ? currentContentRatingTag.label : null}</b>.
                </Fragment>
            );
        },
        getLink: (notification) => createLink("mediaviewerVideo", {
            media: {
                _id: notification?.data?._id,
                slug: notification?.data?.slug
            }
        })
    },
    
    [NOTIFICATION_TYPES.MEDIA.CONTENT_RATING_UPDATED]: {
        icon: (notification) => {
            const currentContentRatingTag = CONTENT_RATINGS_MAP[notification?.data?.contentRating] || null;
            return currentContentRatingTag.icon;
        },
        content: (notification) => {
            const mediaType = notification?.data?.type === "video" ? "vidéo" : "photo";
            const currentContentRatingTag = CONTENT_RATINGS_MAP[notification?.data?.contentRating] || null;
            return <Fragment>
                Votre {mediaType} <b>{cropText(notification.data?.title,50)}</b> à été classée <b>{currentContentRatingTag ? currentContentRatingTag.label : null}</b>.
            </Fragment>
        },
        getLink: (notification) => createLink(notification?.data?.kind === "video" ? "mediaviewerVideo" : "mediaviewerPhoto", {
            media: {
                _id: notification?.data?._id,
                slug: notification?.data?.slug
            }
        })
    }
    
}


const Notification = ({data, onOpen}) => {
    const queryClient = useQueryClient();
    const dataCurrentUser = queryClient.getQueryData([USER_CURRENT_KEY]);
    const currentUserId = _.get(dataCurrentUser, '_id');

    const renderer = renderers[data.type];
    const state = typeof renderer?.getState === "function" ? renderer?.getState(data) :  {};
    const getLink = typeof renderer?.getLink === "function" ? renderer.getLink(data, currentUserId) :  null;
    const icon = typeof renderer?.icon === "function" ? renderer.icon(data, currentUserId) :  renderer.icon;

    const Tag  =  getLink ? Link : "Div";


    const avatarData = !!data?.isSystem
        ? {
            avatar : voissaAvatar,
            gender : 0
        } : {
            avatar : data.sender?.profile?.avatar,
            gender : data.sender?.profile?.gender || "guest"
        }

    return renderer ? (
        <Tag
            to={renderer.getLink(data, currentUserId)}
            state={state}
            className="notification"
            onClick={() => onOpen()}>
                <div className="notification-sender-wrapper">
                    <div className="notification-sender-avatar">
                        <Avatar avatar={avatarData.avatar}
                        height={46}
                        width={46}
                        gender={avatarData.gender}
                        alt=""/>
                    </div>
                    <div className={classNames("notification-icon", renderer?.iconClass)}>
                        {icon && (<FontAwesomeIcon icon={icon}/>)}
                    </div>
                </div>
                <div className="notification-content-wrapper">
                    <div className="notification-content">
                        {renderer.content(data)}
                    </div>
                    <div className="notification-date">
                        {moment(data.fUpdatedAt).fromNow()}
                    </div>
                </div>
                {!data.open && <div className="notification-read-status"></div>}
        </Tag>
    ) : null;
};

export default Notification;
